import React, { useState } from 'react';
import { Box, Typography, Avatar, IconButton, Divider, Tooltip } from '@mui/material';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PublicIcon from '@mui/icons-material/Public';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import DownloadIcon from '@mui/icons-material/Download';

async function downloadImage(url, idx) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const ext = blob.type.includes('png') ? 'png' : blob.type.includes('gif') ? 'gif' : 'jpg';
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `linkedin-bild-${idx + 1}.${ext}`;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch {
    window.open(url, '_blank');
  }
}

const MAX_LEN = 300;

// ── LinkedIn-liknande bildgrid ─────────────────────────────────────────────

function DownloadableImage({ src, idx, imgStyle, boxSx, children }) {
  const [hover, setHover] = useState(false);
  return (
    <Box
      sx={{ position: 'relative', ...boxSx }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <img src={src} alt="" style={imgStyle} />
      {children}
      {hover && (
        <Tooltip title="Ladda ned bild">
          <IconButton
            size="small"
            onClick={e => { e.stopPropagation(); downloadImage(src, idx); }}
            sx={{
              position: 'absolute', bottom: 6, right: 6,
              bgcolor: 'rgba(0,0,0,0.55)',
              color: '#fff',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
            }}
          >
            <DownloadIcon sx={{ fontSize: '1rem' }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

function ImageGrid({ images }) {
  if (!images || images.length === 0) return null;

  const containerSx = { width: '100%', overflow: 'hidden', bgcolor: '#f3f2ef' };
  const imgStyle = { objectFit: 'cover', display: 'block' };

  if (images.length === 1) {
    return (
      <Box sx={containerSx}>
        <DownloadableImage src={images[0]} idx={0}
          imgStyle={{ ...imgStyle, width: '100%', maxHeight: 350 }}
          boxSx={{}} />
      </Box>
    );
  }

  if (images.length === 2) {
    return (
      <Box sx={{ ...containerSx, display: 'flex', gap: '2px' }}>
        {images.map((url, i) => (
          <DownloadableImage key={i} src={url} idx={i}
            imgStyle={{ ...imgStyle, width: '100%', height: 240 }}
            boxSx={{ width: '50%' }} />
        ))}
      </Box>
    );
  }

  if (images.length === 3) {
    return (
      <Box sx={{ ...containerSx, display: 'flex', gap: '2px', height: 260 }}>
        <DownloadableImage src={images[0]} idx={0}
          imgStyle={{ ...imgStyle, width: '100%', height: '100%' }}
          boxSx={{ width: '60%' }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1 }}>
          <DownloadableImage src={images[1]} idx={1}
            imgStyle={{ ...imgStyle, width: '100%', height: '100%' }}
            boxSx={{ flex: 1 }} />
          <DownloadableImage src={images[2]} idx={2}
            imgStyle={{ ...imgStyle, width: '100%', height: '100%' }}
            boxSx={{ flex: 1 }} />
        </Box>
      </Box>
    );
  }

  // 4+: 2×2 grid, sista slot visar "+N till" om fler
  const display = images.slice(0, 4);
  const extra = images.length - 4;
  return (
    <Box sx={{ ...containerSx, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px' }}>
      {display.map((url, i) => (
        <DownloadableImage key={i} src={url} idx={i}
          imgStyle={{ ...imgStyle, width: '100%', height: '100%' }}
          boxSx={{ height: 160 }}>
          {i === 3 && extra > 0 && (
            <Box sx={{
              position: 'absolute', inset: 0,
              bgcolor: 'rgba(0,0,0,0.45)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              pointerEvents: 'none',
            }}>
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.5rem' }}>
                +{extra}
              </Typography>
            </Box>
          )}
        </DownloadableImage>
      ))}
    </Box>
  );
}

// ── Huvud-komponent ────────────────────────────────────────────────────────

export default function LinkedInPreviewCard({
  authorName = 'Tikab Strukturmekanik AB',
  authorTitle = 'Konsultföretag inom teknik • Solna',
  authorAvatar = null,
  content = '',
  imageUrl = null,      // bakåtkompatibilitet
  images = null,        // föredras – array av URL-strängar
  followers = '500+',
  compact = false,
  // Intern tumme upp
  likeCount = 0,
  isLiked = false,
  onLike = null,        // null = mockup-knapp
}) {
  const [expanded, setExpanded] = useState(false);
  const shouldTruncate = content.length > MAX_LEN && !expanded;
  const displayContent = shouldTruncate ? content.slice(0, MAX_LEN) : content;

  // Normalisera bildlista
  const imageList = images && images.length > 0
    ? images
    : imageUrl
      ? [imageUrl]
      : [];

  return (
    <Box sx={{
      bgcolor: '#fff',
      borderRadius: '8px',
      border: '1px solid #e0e0e0',
      boxShadow: '0 0 0 1px rgba(0,0,0,.08), 0 2px 4px rgba(0,0,0,.05)',
      overflow: 'hidden',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, sans-serif',
      maxWidth: compact ? '100%' : 560,
      width: '100%',
    }}>
      {/* Header */}
      <Box sx={{ p: '12px 16px 0', display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Avatar
          src={authorAvatar || undefined}
          sx={{
            width: 48, height: 48,
            bgcolor: authorAvatar ? '#fff' : '#0a66c2',
            fontSize: '1.1rem',
            fontWeight: 700,
            border: '1px solid rgba(0,0,0,0.08)',
            '& img': { objectFit: 'contain', p: '4px' },
          }}
        >
          {!authorAvatar && authorName.charAt(0)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontWeight: 600,
            fontSize: '0.875rem',
            color: 'rgba(0,0,0,0.9)',
            lineHeight: 1.3,
            '&:hover': { color: '#0a66c2', cursor: 'pointer' },
          }}>
            {authorName}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'rgba(0,0,0,0.6)', lineHeight: 1.3, mt: 0.2 }}>
            {authorTitle}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
            <Typography sx={{ fontSize: '0.7rem', color: 'rgba(0,0,0,0.6)' }}>
              1 dag •
            </Typography>
            <PublicIcon sx={{ fontSize: '0.7rem', color: 'rgba(0,0,0,0.6)' }} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {!compact && (
            <IconButton size="small" sx={{ color: 'rgba(0,0,0,0.6)' }}>
              <BookmarkBorderIcon sx={{ fontSize: '1.1rem' }} />
            </IconButton>
          )}
          <IconButton size="small" sx={{ color: 'rgba(0,0,0,0.6)' }}>
            <MoreHorizIcon sx={{ fontSize: '1.1rem' }} />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ px: 2, pt: 1.5, pb: imageList.length > 0 ? 1 : 2 }}>
        {content ? (
          <Typography sx={{
            fontSize: '0.875rem',
            color: 'rgba(0,0,0,0.9)',
            whiteSpace: 'pre-wrap',
            lineHeight: 1.6,
          }}>
            {displayContent}
            {shouldTruncate && (
              <>
                {'…  '}
                <Box
                  component="span"
                  onClick={() => setExpanded(true)}
                  sx={{
                    color: 'rgba(0,0,0,0.6)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  visa mer
                </Box>
              </>
            )}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: '0.875rem', color: 'rgba(0,0,0,0.35)', fontStyle: 'italic' }}>
            Inläggstexten visas här…
          </Typography>
        )}
      </Box>

      {/* Bildgrid */}
      <ImageGrid images={imageList} />

      {/* Stats + knappar */}
      {!compact && (
        <>
          {/* Statsrad */}
          <Box sx={{ px: 2, pt: 1, pb: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ display: 'flex' }}>
                {['👍', '❤️', '💡'].map((e, i) => (
                  <Box key={i} sx={{
                    fontSize: '0.85rem', bgcolor: '#fff', borderRadius: '50%',
                    border: '1px solid #fff', ml: i > 0 ? '-4px' : 0, lineHeight: 1, zIndex: 3 - i,
                  }}>
                    {e}
                  </Box>
                ))}
              </Box>
              {likeCount > 0 && (
                <Typography sx={{ fontSize: '0.75rem', color: 'rgba(0,0,0,0.6)', ml: 0.3 }}>
                  {likeCount}
                </Typography>
              )}
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(0,0,0,0.6)' }}>
              — kommentarer • — delningar
            </Typography>
          </Box>

          <Divider sx={{ mx: 2 }} />

          {/* Åtgärdsknappar */}
          <Box sx={{ display: 'flex', py: 0.25 }}>

            {/* GILLA – funktionell intern knapp */}
            <Tooltip title={onLike ? 'Intern reaktion – signalera att du gillar förslaget' : 'LinkedIn-mockup'}>
              <Box
                onClick={onLike || undefined}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5,
                  flex: 1, justifyContent: 'center',
                  py: 1, px: 0.5, borderRadius: 1,
                  cursor: onLike ? 'pointer' : 'default',
                  color: isLiked ? '#0a66c2' : 'rgba(0,0,0,0.6)',
                  fontWeight: isLiked ? 700 : 400,
                  '&:hover': onLike ? { bgcolor: 'rgba(10,102,194,0.08)' } : {},
                  transition: 'color 0.15s',
                }}
              >
                {isLiked
                  ? <ThumbUpAltIcon sx={{ fontSize: '1.1rem' }} />
                  : <ThumbUpAltOutlinedIcon sx={{ fontSize: '1.1rem' }} />
                }
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 'inherit' }}>
                  Gilla
                </Typography>
                {/* Liten prick som markerar att knappen är aktiv/intern */}
                {onLike && (
                  <Box sx={{
                    width: 5, height: 5, borderRadius: '50%',
                    bgcolor: isLiked ? '#0a66c2' : 'rgba(10,102,194,0.4)',
                    flexShrink: 0,
                  }} />
                )}
              </Box>
            </Tooltip>

            {/* MOCKUP-knappar */}
            {[
              { icon: <ChatBubbleOutlineIcon sx={{ fontSize: '1.1rem' }} />, label: 'Kommentera' },
              { icon: <ShareOutlinedIcon sx={{ fontSize: '1.1rem' }} />, label: 'Dela' },
              { icon: <SendOutlinedIcon sx={{ fontSize: '1.1rem' }} />, label: 'Skicka' },
            ].map(({ icon, label }) => (
              <Tooltip key={label} title="LinkedIn-mockup">
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5,
                  flex: 1, justifyContent: 'center',
                  py: 1, px: 0.5, borderRadius: 1,
                  cursor: 'default',
                  color: 'rgba(0,0,0,0.3)',
                }}>
                  {icon}
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, display: { xs: 'none', sm: 'block' } }}>
                    {label}
                  </Typography>
                </Box>
              </Tooltip>
            ))}
          </Box>

          {/* Diskret förklaringsrad */}
          <Box sx={{ mx: 2, mb: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#0a66c2', flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.6rem', color: 'rgba(0,0,0,0.38)' }}>
              Gilla = intern reaktion &nbsp;·&nbsp; Kommentera / Dela / Skicka = LinkedIn-mockup
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
}
